
import React, { useEffect } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Shield, BookOpen, PenTool, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import FeaturesSection from "@/components/FeaturesSection";
import TransitionSection from "@/components/TransitionSection";
import ScriptureTransitionSection from "@/components/ScriptureTransitionSection";
import PrayerMinistrySection from "@/components/PrayerMinistrySection";
import ApologeticsOverviewSection from "@/components/ApologeticsOverviewSection";
import SpiritualJourneySection from "@/components/SpiritualJourneySection";
import RotatingTestimonials from "@/components/RotatingTestimonials";
import { AboutSection } from "@/components/features/AboutSection";

const Index = () => {
  const {
    scrollY
  } = useScroll();
  const isMobile = useIsMobile();

  // Create dynamic scroll-based animations with significantly delayed mobile timing
  const imageScale = useTransform(scrollY, [0, isMobile ? 600 : 300], [1, 0.8]);
  const imageRotate = useTransform(scrollY, [0, isMobile ? 600 : 300], [0, -10]);
  const imageTranslateY = useTransform(scrollY, [0, isMobile ? 600 : 300], [0, -50]);
  const imageOpacity = useTransform(scrollY, [0, isMobile ? 600 : 300], [1, 0]);

  // Force rerender when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return <>
    {/* Hero Section with gradient background */}
    <div className={`relative w-full min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-[#f1f1f1] to-white`}>
      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 pt-24 md:pt-36 relative z-20 grid md:grid-cols-2 gap-4 items-center">
        {/* Text content */}
        <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: "easeInOut"
        }} className="text-left">
          <motion.h1 className="text-3xl md:text-6xl lg:text-7xl font-playfair" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.8
          }}>
            <span className="block text-black text-3xl md:text-6xl lg:text-7xl whitespace-nowrap">Scripture, Made Personal</span>
            <span className="block text-yellow-500 text-3xl md:text-6xl lg:text-7xl whitespace-nowrap">Truth, Made Clear</span>
          </motion.h1>
          
          <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4,
            duration: 0.8
          }} className="text-black/90 md:text-lg mb-6 md:mb-8 leading-relaxed mt-4 text-xs">Selah bridges timeless Scripture with modern tools, helping you explore the Bible, grow in faith, and stand firm in truth with clarity, confidence, and peace.</motion.p>

            <motion.div className="space-y-4 md:space-y-6 mb-8 md:mb-10" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6,
            duration: 0.8
          }}>
              <motion.div className="flex items-start gap-4 group hover:translate-x-1 transition-transform duration-300" whileHover={{
              scale: 1.02
            }}>
              <div className="text-heaven-gold">
                <BookOpen className="h-4 w-4 md:h-5 md:w-5 animate-pulse" />
              </div>
              <div className="text-black/90 font-medium text-xs md:text-base">
                Get timely Bible verses based on your current needs
              </div>
            </motion.div>
            
            <motion.div className="flex items-start gap-4 group hover:translate-x-1 transition-transform duration-300" whileHover={{
              scale: 1.02
            }}>
              <div className="text-heaven-gold">
                <Shield className="h-4 w-4 md:h-5 md:w-5 animate-pulse" />
              </div>
              <div className="text-black/90 font-medium text-xs md:text-base">
                Defend your faith with tools rooted in Scripture
              </div>
            </motion.div>
            
            <motion.div className="flex items-start gap-4 group hover:translate-x-1 transition-transform duration-300" whileHover={{
              scale: 1.02
            }}>
              <div className="text-heaven-gold">
                <PenTool className="h-4 w-4 md:h-5 md:w-5 animate-pulse" />
              </div>
              <div className="text-black/90 font-medium text-xs md:text-base">
                Generate personalized prayers for any moment
              </div>
            </motion.div>
            
            <motion.div className="flex items-start gap-4 group hover:translate-x-1 transition-transform duration-300" whileHover={{
              scale: 1.02
            }}>
              <div className="text-heaven-gold">
                <Brain className="h-4 w-4 md:h-5 md:w-5 animate-pulse" />
              </div>
              <div className="text-black/90 font-medium text-xs md:text-base">
                Explore complex topics with biblical clarity and grace
              </div>
            </motion.div>
          </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8,
            duration: 0.8
          }} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }}>
              <Button variant="gold" size="xl" className="hidden md:flex rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden group">
                <span className="relative z-10 text-neutral-50">Explore Biblical Tools</span>
                <span className="absolute inset-0 bg-gradient-to-r from-heaven-gold to-amber-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </Button>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.8,
              duration: 0.8
            }} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="flex justify-center" // Add this to center the button
            >
                <Button variant="gold" size="lg" className="md:hidden mx-auto rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden group" onClick={() => window.location.href = "/sign-up"}>
                  <span className="relative z-10">Get Started</span>
                  <span className="absolute inset-0 bg-gradient-to-r from-heaven-gold to-amber-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        
          {/* Logo Section with animated hero image */}
          <motion.div className="flex justify-start items-center pl-0 md:pl-4 perspective-1000" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: "easeInOut"
        }}>
          <motion.div style={{
            scale: imageScale,
            rotate: imageRotate,
            y: imageTranslateY,
            opacity: imageOpacity
          }} className="relative w-full">
            <motion.img src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//finalheroooooodhdhd.png" alt="Hero Image" className="max-w-[90%] h-auto relative z-10" whileHover={{
              scale: 1.05,
              transition: {
                duration: 0.3
              }
            }} animate={{
              y: [0, -10, 0]
            }} transition={{
              y: {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }
            }} />
            <motion.div className="absolute -inset-4 bg-heaven-gold/10 rounded-full blur-2xl" animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.8, 0.5]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }} />
          </motion.div>
        </motion.div>
      </div>
    </div>

    {/* Testimonial Section */}
    <motion.div initial={{
      opacity: 0,
      y: 50
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: "-100px"
    }} transition={{
      duration: 0.6,
      ease: "easeOut"
    }} className="w-full bg-white flex items-center justify-center py-32 md:py-[30px]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <RotatingTestimonials />
        </div>
      </motion.div>

    {/* About Section */}
    <AboutSection />
    
    {/* Restored TransitionSection */}
    <TransitionSection />
    
    {/* Features Section */}
    <FeaturesSection />
    
    {/* The rest of the sections */}
    <ScriptureTransitionSection />
    <PrayerMinistrySection />
    <ApologeticsOverviewSection />
    <SpiritualJourneySection />
  </>;
};
export default Index;
